import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Spinner, Button, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import { FaMoneyBillWave, FaEye, FaTicketAlt, FaCheckCircle, FaMapMarkerAlt, FaCalendarAlt, FaDownload, FaFilter, FaEllipsisV } from "react-icons/fa";
import { fetchTransactions, fetchSpots } from "../../redux/actions/TicketActions";

const TransactionHistory = () => {
  const dispatch = useDispatch();
  const {
    TicketReducer: { tickets: transactions, count: transactionsCount, loading },
  } = useSelector((state) => state);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [paginate, setPaginate] = useState(10);
  const [ticketType, setTicketType] = useState("all");
  const [transactionType, setTransactionType] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [place, setPlace] = useState("all");
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const {
    TicketReducer: { spots },
  } = useSelector((state) => state);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: paginate,
      search,
      sort,
      ticketType: ticketType !== 'all' ? ticketType : undefined,
      transactionType: transactionType !== 'all' ? transactionType : undefined,
      status: status !== 'all' ? status : undefined,
      dateRange: dateRange !== 'all' ? dateRange : undefined,
      place: place !== 'all' ? place : undefined,
      invoice: invoiceFilter !== 'all' ? invoiceFilter : undefined,
      startDate: customStartDate || undefined,
      endDate: customEndDate || undefined
    };

    dispatch(fetchTransactions(params));
  }, [dispatch, paginate, page, search, sort, ticketType, transactionType, status, dateRange, place, invoiceFilter, customStartDate, customEndDate]);

  useEffect(() => {
    dispatch(fetchSpots());
  }, [dispatch]);



  const handleExportCSV = () => {
    if (!transactions || transactions.length === 0) return;
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Transaction ID,Ticket ID,Customer,Date,Type,Status,Amount\n" +
      transactions
        .map(
          (t) =>
            `${t.id},${t.orderId || "N/A"},${t.user?.name},${new Date(
              t.createdAt
            ).toLocaleDateString()},${t.type},${t.paymentStatus},₹${t.amount}`
        )
        .join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    if (!transactions || transactions.length === 0) return;
    
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('Transaction History Report', 20, 20);
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    
    const headers = ['S.No', 'Transaction ID', 'Ticket ID', 'Customer', 'Date', 'Type', 'Status', 'Amount'];
    const colWidths = [15, 25, 25, 30, 25, 20, 20, 25];
    let xPos = 10;
    let yPosition = 50;
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    headers.forEach((header, index) => {
      doc.text(header, xPos, yPosition);
      xPos += colWidths[index];
    });
    
    yPosition += 10;
    doc.setFont(undefined, 'normal');
    
    transactions.slice(0, 50).forEach((transaction, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      
      xPos = 10;
      const rowData = [
        (index + 1).toString(),
        transaction.id.toString(),
        transaction.orderId || 'N/A',
        transaction.user?.name || 'N/A',
        new Date(transaction.createdAt).toLocaleDateString(),
        transaction.type || 'N/A',
        transaction.paymentStatus || 'N/A',
        `₹${transaction.amount || 0}`
      ];
      
      rowData.forEach((data, colIndex) => {
        let text = data;
        if (colIndex === 1 && text.length > 10) text = text.substring(0, 10) + '...';
        if (colIndex === 3 && text.length > 12) text = text.substring(0, 12) + '...';
        
        doc.text(text, xPos, yPosition);
        xPos += colWidths[colIndex];
      });
      
      yPosition += 8;
    });
    
    const totalAmount = transactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    yPosition += 10;
    doc.setFont(undefined, 'bold');
    doc.text(`Total Amount: ₹${totalAmount.toFixed(2)}`, 20, yPosition);
    
    doc.save('transaction-history.pdf');
  };

  const clearFilters = () => {
    setSearch("");
    setTicketType("all");
    setTransactionType("all");
    setStatus("all");
    setDateRange("all");
    setPlace("all");
    setInvoiceFilter("all");
    setSort("desc");
    setPage(0);
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">
              <FaMoneyBillWave className="me-2" />
              Transaction History
            </h5>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="flex-grow-1" style={{ maxWidth: "190px" }}>
                <Input
                  type="text"
                  className="form-control"
                  placeholder="🔍 Search by ID, Visitor, Phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="d-flex flex-wrap gap-2">
                <div className="position-relative">
                  <FaTicketAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
                  <Input
                    type="select"
                    value={ticketType}
                    onChange={(e) => setTicketType(e.target.value)}
                    style={{ width: "140px", paddingLeft: '30px' }}
                  >
                    <option value="all">Ticket type</option>
                    <option value="online">Booking</option>
                    <option value="counter">Counter</option>
                  </Input>
                </div>
                <div className="position-relative">
                  <FaCheckCircle className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
                  <Input
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{ width: "170px", paddingLeft: '30px' }}
                  >
                    <option value="all">Payment Status</option>
                    <option value="pending">Pending</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </Input>
                </div>
                <div className="position-relative">
                  <FaMapMarkerAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
                  <Input
                    type="select"
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    style={{ width: "140px", paddingLeft: '30px' }}
                  >
                    <option value="all">All Spots</option>
                    {spots.map((spot) => (
                      <option key={spot} value={spot}>
                        {spot}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="position-relative">
                  <FaCalendarAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
                  <Input
                    type="select"
                    value={dateRange}
                    onChange={(e) => {
                      setDateRange(e.target.value);
                      setShowCustomRange(e.target.value === "custom");
                    }}
                    style={{ width: "140px", paddingLeft: '30px' }}
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="custom">Custom Range</option>
                  </Input>
                </div>
                <Input
                  type="select"
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="all">All Types</option>
                  <option value="booking">Booking</option>
                  <option value="refund">Refund</option>
                  <option value="settlement">Settlement</option>
                </Input>
                <Input
                  type="select"
                  value={invoiceFilter}
                  onChange={(e) => setInvoiceFilter(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="all">All Invoices</option>
                  <option value="generated">Generated</option>
                  <option value="pending">Pending</option>
                </Input>
                <Dropdown
                  isOpen={exportDropdownOpen}
                  toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                >
                  <DropdownToggle className="btn btn-outline-secondary text-white">
                    <FaDownload className="me-1" />
                    Export
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={handleExportCSV}>
                      Export CSV
                    </DropdownItem>
                    <DropdownItem onClick={handleExportPDF}>
                      Export PDF
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <button
                  className="btn btn-outline-danger"
                  onClick={clearFilters}
                >
                  <FaFilter className="me-1" />
                  Clear Filters
                </button>
              </div>
            </div>
            {showCustomRange && (
              <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
                <label className="fw-bold mb-0">From:</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{ width: "150px" }}
                />
                <label className="fw-bold mb-0">To:</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{ width: "150px" }}
                />
              </div>
            )}
        <div className="row">
          <div className="col-md-12">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Transaction ID</th>
                  <th>Ticket ID</th>
                  <th>Transaction Type</th>
                  <th>Description</th>
                  <th>User/Partner</th>
                  <th>Mobile</th>
                  <th>Date</th>
                  <th>Payment Method</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center">No data found</td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr key={transaction.id}>
                      <th scope="row">{index + 1 + page * paginate}</th>
                      <td>{transaction.orderId}</td>
                      <td>{transaction.id}</td>
                      <td>{transaction.transaction_type || 'Booking'}</td>
                      <td>{transaction.description || transaction.remarks || 'Ticket booking payment'}</td>
                      <td>{transaction.user?.name || transaction.partner?.businessName || 'N/A'}</td>
                      <td>{transaction.user?.mobile || transaction.phoneNo}</td>
                      <td>{new Date(transaction.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(transaction.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                      <td>{transaction.gatewayType}</td>
                      <td>₹{transaction.amount}</td>
                      <td>
                        <span className={`badge ${
                          transaction.paymentStatus === 'success' ? 'bg-success' : 
                          transaction.paymentStatus === 'pending' ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {transaction.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <Dropdown
                          isOpen={actionDropdowns[transaction.id] || false}
                          toggle={() => setActionDropdowns(prev => ({ ...prev, [transaction.id]: !prev[transaction.id] }))}
                        >
                          <DropdownToggle className="btn btn-sm btn-outline-secondary">
                            <FaEllipsisV style={{ color: 'white' }} />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem>
                              <FaEye className="me-2" /> View
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                id="paginate"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={transactionsCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;